// ChangLogs頁面
import React, { useState, useEffect, useRef } from 'react';
import api from '../config/axiosConfig';
import styles from './css/ChangeLog.module.css';
import { 
  Box, Button, Chip, Container, Grid, Typography, CircularProgress, 
  IconButton, TextField, InputAdornment, FormControl, 
  Select, MenuItem, InputLabel, Fade, Tooltip, Divider, useMediaQuery, useTheme, Alert 
} from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent';
import { 
  AutoFixHigh, BugReport, Construction, Delete, Grade, LayersClear, 
  Settings, TaskAlt, KeyboardArrowUp, Search, FilterAlt, Clear, 
  OpenInNew, Link as LinkIcon, Home 
} from '@mui/icons-material';

function ChangeLog() {
  const [changeLogs, setChangeLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [availableVersions, setAvailableVersions] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const timelineRef = useRef(null);

  useEffect(() => {
    document.title = '變更日誌';
    
    // 監聽滾動事件
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    const fetchChangeLogs = async () => {
      try {
        setLoading(true);
        const response = await api.get('/core/api/ChangeLog/');
        const sortedLogs = response.data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        setChangeLogs(sortedLogs);
        setFilteredLogs(sortedLogs);
        
        // 提取可用的版本列表
        const versions = [...new Set(sortedLogs.map(log => log.version))];
        setAvailableVersions(versions);
        
        // 檢查URL是否有哈希值，有則滾動到對應版本
        setTimeout(() => {
          if (window.location.hash) {
            const id = window.location.hash.substring(1);
            const element = document.getElementById(id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 500);
      } catch (error) {
        console.error('Fetch Change Logs Error:', error);
        setError('無法載入變更日誌，請稍後再試。');
      } finally {
        setLoading(false);
      }
    };

    fetchChangeLogs();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 過濾日誌
  useEffect(() => {
    if (changeLogs.length === 0) return;
    
    let filtered = [...changeLogs];
    
    // 按版本過濾
    if (versionFilter) {
      filtered = filtered.filter(log => log.version === versionFilter);
    }
    
    // 按類型過濾
    if (typeFilter) {
      filtered = filtered.filter(log => 
        log.change_descriptions.some(desc => desc.change_type === typeFilter)
      );
    }
    
    // 按搜索詞過濾
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(log => 
        log.title.toLowerCase().includes(term) || 
        log.log_content.toLowerCase().includes(term) ||
        log.version.toLowerCase().includes(term) ||
        log.change_descriptions.some(desc => 
          desc.change_description.toLowerCase().includes(term)
        )
      );
    }
    
    setFilteredLogs(filtered);
  }, [changeLogs, searchTerm, versionFilter, typeFilter]);

  // 返回頂部功能
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // 重置過濾器
  const resetFilters = () => {
    setSearchTerm('');
    setVersionFilter('');
    setTypeFilter('');
  };

  // 複製版本鏈接到剪貼板
  const copyVersionLink = (version) => {
    const currentUrl = window.location.href.split('#')[0];
    const versionUrl = `${currentUrl}#${version.replace(/\./g, '_')}`;
    
    navigator.clipboard.writeText(versionUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // 格式化版本ID，用於錨點
  const formatVersionId = (version) => {
    return version.replace(/\./g, '_');
  };

  return (
    <div>
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography className={styles['title-tip']}>變更日誌</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Typography className={styles.title}>系統更新及修復</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Typography className={styles.description}>
            這裡將會詳細記錄系統每次更新的內容，包括新增功能、問題修復和其他改進。
            這些變更日誌旨在讓使用者能更快速的了解系統的最新狀況，以及每個版本所做的具體變更。
            如果有任何疑問或需要進一步了解的地方，請隨時聯繫相關負責人。
          </Typography>
        </Box>

        {/* 搜尋與過濾區塊 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="搜尋更新內容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>版本</InputLabel>
                <Select
                  value={versionFilter}
                  onChange={(e) => setVersionFilter(e.target.value)}
                  label="版本"
                >
                  <MenuItem value="">全部版本</MenuItem>
                  {availableVersions.map((version) => (
                    <MenuItem key={version} value={version}>{version}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>變更類型</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="變更類型"
                >
                  <MenuItem value="">全部類型</MenuItem>
                  <MenuItem value="新增">新增功能</MenuItem>
                  <MenuItem value="改進">功能改進</MenuItem>
                  <MenuItem value="修復">功能修復</MenuItem>
                  <MenuItem value="刪除">功能刪除</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title="重置過濾器">
                <IconButton 
                  onClick={resetFilters} 
                  color="primary" 
                  className={styles.resetButton}
                  sx={{ width: '100%', height: '100%' }}
                  disabled={!searchTerm && !versionFilter && !typeFilter}
                >
                  <FilterAlt />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          
          {/* 版本快速導航 */}
          {!isMobile && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableVersions.map((version) => (
                <Chip
                  key={version}
                  label={version}
                  component="a"
                  href={`#${formatVersionId(version)}`}
                  clickable
                  color={versionFilter === version ? "primary" : "default"}
                  size="small"
                  onClick={() => setVersionFilter(version)}
                  icon={<OpenInNew fontSize="small" />}
                  className={styles.versionChip}
                />
              ))}
            </Box>
          )}
          
          {/* 過濾結果統計 */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || versionFilter || typeFilter ? 
                `找到 ${filteredLogs.length} 個符合條件的更新` : 
                `共 ${changeLogs.length} 個更新`}
            </Typography>
            
            {copySuccess && (
              <Fade in={copySuccess}>
                <Alert severity="success" sx={{ py: 0 }}>已複製連結到剪貼板</Alert>
              </Fade>
            )}
          </Box>
          
          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* 載入中狀態 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : filteredLogs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8, flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              沒有找到符合條件的更新
            </Typography>
            <Button variant="outlined" onClick={resetFilters} startIcon={<Home />}>
              返回查看所有更新
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }} ref={timelineRef} className={styles.timelineContainer}>
            <Timeline
              sx={{
                [`& .${timelineOppositeContentClasses.root}`]: {
                  flex: 0.2,
                },
              }}
              className={styles.timeline}
            >
              {filteredLogs.map((changeLog, index) => (
                <TimelineItem key={index} id={formatVersionId(changeLog.version)} className={styles.timelineItem}>
                  <TimelineOppositeContent color="textSecondary">
                    <Box sx={{ display: 'flex', flexDirection: 'column' }} className={styles.timelineDate}>
                      <Typography className={styles['log-date']}>
                        {new Date(changeLog.created_at).toLocaleDateString('zh-TW', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip className={styles['log-version']} size='small' label={changeLog.version}/>
                        <Tooltip title="複製此版本連結">
                          <IconButton 
                            size="small" 
                            onClick={() => copyVersionLink(changeLog.version)}
                            className={styles.linkCopyButton}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot className={styles['timeline-dot']}/>
                    {index < filteredLogs.length - 1 && <TimelineConnector className={styles['timeline-connector']} />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: '20px', mb: '64px' }} className={styles.logContent}>
                      <Typography className={styles['log-title']}>{changeLog.title}</Typography>
                      <Typography className={styles['log-content']}>{changeLog.log_content}</Typography>

                      {changeLog.change_descriptions.some((description) => description.change_type === '新增') && (
                        <Box className={styles.sectionWrapper}>
                          <Box className={styles['log-description-title']}>
                            <Typography className={styles['log-description-title-text']}>
                              <Grade className={styles['grade-icon']}/>
                              新增功能
                            </Typography>
                            <Typography className={styles['log-description-description']}>
                              提供新加入的功能或事項的詳細說明，以及這些新增將如何幫助用戶提升效率或體驗：
                            </Typography>
                          </Box>

                          <Box className={styles['log-description-add-part']}>
                            <Grid container sx={{ padding: '24px 24px 14px 24px'}}>
                              {changeLog.change_descriptions.filter((description) => description.change_type === '新增').map((description, index) => (
                              <Grid item xs={12} key={index} className={styles['log-description']}>
                                <TaskAlt className={styles['task-icon']}/>
                                {description.change_description}
                              </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      )}

                      {changeLog.change_descriptions.some((description) => description.change_type === '改進') && (
                        <Box className={styles.sectionWrapper}>
                          <Box className={styles['log-description-title']}>
                            <Typography className={styles['log-description-title-text']}>
                              <Settings className={styles['settings-icon']}/>
                              功能改進
                            </Typography>
                            <Typography className={styles['log-description-description']}>
                              說明對現有功能進行的改良點，包括效能提升或用戶界面的優化細節：
                            </Typography>
                          </Box>

                          <Box className={styles['log-description-improve-part']}>
                            <Grid container sx={{ padding: '24px 24px 14px 24px'}}>
                              {changeLog.change_descriptions.filter((description) => description.change_type === '改進').map((description, index) => (
                              <Grid item xs={12} key={index} className={styles['log-description']}>
                                <Construction className={styles['construction-icon']}/>
                                {description.change_description}
                              </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      )}

                      {changeLog.change_descriptions.some((description) => description.change_type === '修復') && (
                        <Box className={styles.sectionWrapper}>
                          <Box className={styles['log-description-title']}>
                            <Typography className={styles['log-description-title-text']}>
                              <BugReport className={styles['bugreport-icon']}/>
                              功能修復
                            </Typography>
                            <Typography className={styles['log-description-description']}>
                              列出已修復的錯誤或問題，並詳述這些修復如何影響用戶的操作流程：
                            </Typography>
                          </Box>

                          <Box className={styles['log-description-fix-part']}>
                            <Grid container sx={{ padding: '24px 24px 14px 24px'}}>
                              {changeLog.change_descriptions.filter((description) => description.change_type === '修復').map((description, index) => (
                              <Grid item xs={12} key={index} className={styles['log-description']}>
                                <AutoFixHigh className={styles['autofixhigh-icon']}/>
                                {description.change_description}
                              </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      )}

                      {changeLog.change_descriptions.some((description) => description.change_type === '刪除') && (
                        <Box className={styles.sectionWrapper}>
                          <Box className={styles['log-description-title']}>
                            <Typography className={styles['log-description-title-text']}>
                              <Delete className={styles['delete-icon']}/>
                              功能刪除或棄用
                            </Typography>
                            <Typography className={styles['log-description-description']}>
                              說明哪些功能或事項被移除或停用，包括原因和對用戶的可能影響：
                            </Typography>
                          </Box>

                          <Box className={styles['log-description-remove-part']}>
                            <Grid container sx={{ padding: '24px 24px 14px 24px'}}>
                              {changeLog.change_descriptions.filter((description) => description.change_type === '刪除').map((description, index) => (
                              <Grid item xs={12} key={index} className={styles['log-description']}>
                                <LayersClear className={styles['layersclear-icon']}/>
                                {description.change_description}
                              </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      )}

                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Box>
        )}
      </Container>

      <footer className={styles.footer}>
        © 2023 - {new Date().getFullYear()} 小滿科技有限公司 版權所有
      </footer>

      {/* 回到頂部按鈕 */}
      <Fade in={showScrollTop}>
        <Box 
          className={styles.scrollTopButton}
          onClick={scrollToTop}
          role="presentation"
        >
          <Tooltip title="回到頂部">
            <IconButton color="primary" aria-label="回到頂部">
              <KeyboardArrowUp />
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>
    </div>
  );
}

export default ChangeLog;
